import { useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { ConfirmDialogQuestion } from "@/components/ConfirmDialog";
import { DetailStackSharedContext } from "./detailStack.context";
import type { DetailStackTopOffset } from "./detailStack.context";
import { useDetailStackDrawer } from "./detailStack.context";

const toHeight = (topOffset: DetailStackTopOffset) => {
  if (typeof topOffset === "number") {
    return `calc(100% - ${topOffset}px)`;
  }
  const heights: Record<string, string> = {};
  for (const [breakpoint, offset] of Object.entries(topOffset)) {
    heights[breakpoint] = `calc(100% - ${offset}px)`;
  }
  return heights;
};

interface DetailDrawerProps {
  children: ReactNode;
  title: ReactNode;
  /** Actions pinned under the content (e.g. Cancel / Run now). */
  footer?: ReactNode;
  /** Asked before × / Esc / the backdrop closes it, e.g. "Discard this draft?" while a form has changes. */
  confirmClose?: ConfirmDialogQuestion | null;
}

/**
 * The shell of one detail-stack drawer: a title and close button, then scrolling content. Its
 * position comes from the stack. × / Esc closes one level, clicking a drawer's visible strip
 * focuses it, and the page strip closes the whole stack; each close animates, then moves the URL.
 * With `confirmClose`, closing asks first; `footer` pins actions under the content.
 *
 *   <DetailDrawer title={…}>{content}</DetailDrawer>
 */
export const DetailDrawer = (props: DetailDrawerProps) => {
  const { children, title, footer, confirmClose = null } = props;
  const navigate = useNavigate();
  const level = useDetailStackDrawer();
  const shared = useContext(DetailStackSharedContext);
  const collapse = shared?.collapse ?? null;
  const topOffset = shared?.topOffset ?? 0;
  const [isMounted, setIsMounted] = useState(false);
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);
  const isCollapsing = collapse !== null && level.depth > collapse.depth;
  const isOpen = isMounted && !isCollapsing;

  const handleClose = () => {
    if (confirmClose) {
      setIsConfirmingClose(true);
      return;
    }
    level.close();
  };

  const handleConfirmClose = () => {
    setIsConfirmingClose(false);
    level.close();
  };

  const handleCancelClose = () => {
    setIsConfirmingClose(false);
  };

  const handlePaperClick = () => {
    if (!level.isTop) {
      level.focus();
    }
  };

  const handleExited = () => {
    if (level.isTop && collapse) {
      navigate(collapse.to);
    }
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={isOpen}
      sx={{
        pointerEvents: level.isBottom ? undefined : "none",
        top: topOffset,
      }}
      slotProps={{
        backdrop: {
          sx: (theme) => ({
            bgcolor: "rgba(16, 24, 40, 0.28)",
            top: topOffset,
            ...theme.applyStyles("dark", {
              bgcolor: "rgba(4, 8, 11, 0.55)",
            }),
          }),
        },
        paper: {
          onClick: handlePaperClick,
          sx: {
            bgcolor: "background.default",
            backgroundImage: "none",
            borderLeft: 1,
            borderColor: "divider",
            cursor: level.isTop ? undefined : "pointer",
            height: toHeight(topOffset),
            pointerEvents: "auto",
            right: level.offsetPx,
            top: topOffset,
            transition: "right 240ms ease-out",
            width: level.width,
            "&::after": {
              bgcolor: "rgba(0, 0, 0, 0.28)",
              content: "\"\"",
              inset: 0,
              opacity: level.isTop ? 0 : 1,
              pointerEvents: "none",
              position: "absolute",
              transition: "opacity 240ms ease-out",
            },
          },
        },
        transition: {
          onExited: handleExited,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
          px: 3,
          py: 2,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          {title}
        </Box>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 3,
        }}
      >
        {children}
      </Box>
      {footer && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            borderTop: 1,
            borderColor: "divider",
            justifyContent: "flex-end",
            px: 3,
            py: 1.5,
          }}
        >
          {footer}
        </Stack>
      )}
      <ConfirmDialog
        onCancel={handleCancelClose}
        onConfirm={handleConfirmClose}
        question={isConfirmingClose ? confirmClose : null}
      />
    </Drawer>
  );
};
